import * as React from "react";
import { cn } from "../lib/cn.js";

/**
 * PageHeader — Phase B2.4 layout shell.
 *
 * Standard page header used across both portals. Provides a slot for
 * breadcrumbs (or a back link), a heading, an optional description,
 * and a right-aligned actions slot. Sticks to the top of its scroll
 * container by default so primary CTAs (e.g. "Create new tenant",
 * "Send admin invite") stay visible while the page body scrolls.
 *
 * Composition:
 *   <PageHeader>
 *     <PageHeaderTop>
 *       <Breadcrumb> ... </Breadcrumb>
 *       <PageHeaderActions> ... </PageHeaderActions>
 *     </PageHeaderTop>
 *     <PageHeaderTitle>RocketLatestTest</PageHeaderTitle>
 *     <PageHeaderDescription>Tenant detail · School</PageHeaderDescription>
 *   </PageHeader>
 *
 * Why not flatten everything into a single `<PageHeader>` with props?
 * Because the actions slot frequently needs to render conditional
 * mutations / dialogs that own their own state (e.g. the
 * SendAdminInviteDialog). A composable shell stays out of the way.
 *
 * Sticky behaviour: the header is `sticky top-0 z-10` against its
 * scroll container (`<main>`), so it pins flush with the Topbar's
 * bottom edge the moment scroll engages. The Topbar above is
 * `position: fixed; top-0` so when both are present the PageHeader
 * sits directly underneath the Topbar.
 *
 * Vertical breathing room: the header owns its own `pt-3` (= 12px)
 * top padding, so the breadcrumb sits 12px below the Topbar at
 * scroll=0 AND while scrolled. The previous iteration relied on
 * `<main>`'s `pt-3` + `-top-3` on the sticky header — that produced
 * a 0-12-0 jitter as scroll engaged (gap collapsed to 0 the moment
 * the header docked) which read as the breadcrumb "scrolling under"
 * the Topbar. Owning the padding internally keeps the gap rock-solid.
 *
 * History: pt-8/-top-8 (32px, B2.4) → pt-3/-top-3 (12px, B2.5d) →
 * pt-3 inside header + main pt-0 (12px stable, B2.6 polish).
 */
interface PageHeaderProps extends React.ComponentPropsWithoutRef<"header"> {
  /**
   * When true (default), the header is `position: sticky` directly
   * under the Topbar with horizontal bleed and a backdrop blur — the
   * Phase B2.4 hotfix behaviour for pages where `<main>` scrolls.
   *
   * When false, the header renders as a normal block element — use
   * this on pages that opt into the fixed-frame flex layout (where
   * the page itself never scrolls and the table fills the viewport,
   * Iron Rule #16). Sticky in a non-scrolling context overlaps the
   * DataTable below it; non-sticky stays in flex flow cleanly.
   */
  sticky?: boolean;
}

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  ({ className, sticky = true, ...props }, ref) => (
    <header
      ref={ref}
      className={cn(
        "grid gap-2",
        sticky && [
          // Sticky-pin the header inside `<main>` (the scroll
          // container) at `top-0`. Combined with the header's own
          // `pt-3`, the breadcrumb sits 12px below the Topbar at
          // every scroll position — no jitter, no docking-flush.
          //
          // CRITICAL: pair this with main `pt-0`. The 12px gap is
          // OWNED by this header's pt-3, not borrowed from main.
          "bg-background sticky top-[var(--app-top-inset,0px)] z-10",
          // Hairline border so the sticky header has a clear edge
          // once the page content scrolls past it.
          "border-border/60 border-b",
        ],
        // Vertical padding lives on the header itself — this gives
        // `sticky=false` pages (e.g. table layouts where the page
        // never scrolls) the same 12px top breathing room without
        // any external padding wiring.
        "pb-3 pt-3",
        className,
      )}
      {...props}
    />
  ),
);
PageHeader.displayName = "PageHeader";

const PageHeaderTop = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      // `items-start` (not items-center): the breadcrumb hugs the top
      // of its flex row regardless of how tall the actions row is. With
      // items-center, a tall PageHeaderActions (e.g. default-size
      // Buttons ~36px) would visually shift the breadcrumb ~8px down,
      // making the topbar→breadcrumb gap inconsistent across pages
      // (12px on a page with no actions, 20px on a page with buttons).
      // `items-start` keeps the gap rock-solid at 12px everywhere.
      className={cn("flex flex-wrap items-start justify-between gap-3", className)}
      {...props}
    />
  ),
);
PageHeaderTop.displayName = "PageHeaderTop";

const PageHeaderActions = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-wrap items-center gap-2", className)} {...props} />
  ),
);
PageHeaderActions.displayName = "PageHeaderActions";

const PageHeaderTitle = React.forwardRef<HTMLHeadingElement, React.ComponentPropsWithoutRef<"h1">>(
  ({ className, ...props }, ref) => (
    <h1
      ref={ref}
      className={cn(
        "text-foreground text-2xl font-semibold leading-tight tracking-tight",
        className,
      )}
      {...props}
    />
  ),
);
PageHeaderTitle.displayName = "PageHeaderTitle";

const PageHeaderDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<"p">
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-muted-foreground text-sm", className)} {...props} />
));
PageHeaderDescription.displayName = "PageHeaderDescription";

/**
 * Optional helper that wraps title + description so callers don't
 * have to repeat the wrapping `<div>` everywhere.
 */
const PageHeaderHeading = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("grid gap-1", className)} {...props} />
  ),
);
PageHeaderHeading.displayName = "PageHeaderHeading";

export {
  PageHeader,
  PageHeaderTop,
  PageHeaderActions,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderHeading,
};
